import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toaster } from 'react-hot-toast';
import { useReactFlow } from 'reactflow';
import WorkflowEditor from './components/WorkflowEditor';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ProjectModal from './components/ProjectModal';
import ExecutionPanel from './components/ExecutionPanel';
import { useProjectStore } from './stores/projectStore';
import { useExecutionStore } from './stores/executionStore';
import { useAuthStore } from './stores/authStore';
import toast from 'react-hot-toast';
import './App.css';
import { useDnD } from './stores/DnDcontext';

function App() {
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showExecutionPanel, setShowExecutionPanel] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [dragNodeData] = useDnD();

  const {
    isAuthenticated,
    isChecking,
    isLoggingIn,
    login,
    logout,
    checkAuth,
  } = useAuthStore();
  
  const { 
    currentProject, 
    loadProjects, 
    createProject, 
    loadProject,
    addNode,
  } = useProjectStore();
  
  const { 
    executions, 
    connectWebSocket,
    disconnectWebSocket,
    restoreExecutions 
  } = useExecutionStore();

  const { screenToFlowPosition } = useReactFlow();
  const reactFlowWrapper = useRef(null);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // 認証後の初期化処理
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // プロジェクト読み込み
    loadProjects();
    
    // WebSocket接続
    connectWebSocket();
    
    // 実行中ジョブの復元
    restoreExecutions();
    
    // 最初のプロジェクトがない場合はモーダル表示
    const savedProjects = localStorage.getItem('ai-studio-projects');
    if (!savedProjects || JSON.parse(savedProjects).length === 0) {
      setShowProjectModal(true);
    }
  }, [isAuthenticated, loadProjects, connectWebSocket, restoreExecutions]);

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    try {
      await login(username, password);
      setPassword('');
      toast.success('ログインしました');
    } catch (error) {
      toast.error(error.message || 'ログインに失敗しました');
    }
  };

  const handleLogout = async () => {
    await logout();
    disconnectWebSocket();
    setShowExecutionPanel(false);
    toast.success('ログアウトしました');
  };

  // プロジェクト作成
  const handleCreateProject = (projectData) => {
    const project = createProject(projectData);
    loadProject(project.id);
    setShowProjectModal(false);
  };

  // 実行中のジョブがある場合は実行パネルを表示
  useEffect(() => {
    const runningExecutions = Object.values(executions).filter(
      exec => exec.status === 'running' || exec.status === 'started'
    );
    setShowExecutionPanel(runningExecutions.length > 0);
  }, [executions]);

  // ノードクリックで追加
  const handleAddNode = useCallback((nodeType, config, position) => {
    let finalPosition = position;

    if(!finalPosition) {
      const rect = reactFlowWrapper.current.getBoundingClientRect();
      finalPosition = screenToFlowPosition({
        x: rect.width * 5 / 8 ,
        y: rect.height * 5 / 12 ,
      });
    }

    const nodeData = {
      nodeType,
      label: config.name,
      config,
      position: finalPosition,
    };
    
    addNode(nodeData);
    toast.success(`"${config.name}" ノードを追加しました`);
  }, [screenToFlowPosition, addNode]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  },[]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if(!dragNodeData){
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX - 110,
        y: event.clientY - 50,
      });

      handleAddNode(dragNodeData.nodeType, dragNodeData.config, position);
    },
    [screenToFlowPosition, dragNodeData, handleAddNode],
  );

  if (isChecking) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-700 mb-2">認証状態を確認中...</div>
          <p className="text-gray-500">しばらくお待ちください</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">AI Data Studio</h1>
          <p className="text-gray-500 mb-6">続行するにはログインしてください</p>

          <form className="space-y-4" onSubmit={handleLoginSubmit}>
            <div>
              <label className="block text-sm text-gray-700 mb-1">ユーザー名</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-studio-500"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">パスワード</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-studio-500"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-studio-500 text-white py-2.5 rounded-lg hover:bg-studio-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isLoggingIn ? 'ログイン中...' : 'ログイン'}
            </button>
          </form>
        </div>
        <Toaster position="top-right" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* ヘッダー */}
      <Header 
        onNewProject={() => setShowProjectModal(true)}
        onToggleExecution={() => setShowExecutionPanel(!showExecutionPanel)}
        onLogout={handleLogout}
        hasRunningJobs={Object.values(executions).some(
          exec => exec.status === 'running' || exec.status === 'started'
        )}
      />
      
      {/* メインコンテンツ */}
      <div className="flex-1 flex min-h-0">
        {/* サイドバー */}
        <Sidebar onNodeAdd={handleAddNode}/>
        
        {/* ワークフローエディタ */}
        <div className="flex-1 relative min-h-0" ref={reactFlowWrapper}>
          {currentProject ? (
            <WorkflowEditor onDragOver={onDragOver} onDrop={onDrop}/>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">🎯</div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">
                  AI Data Studio へようこそ
                </h2>
                <p className="text-gray-500 mb-6">
                  新しいプロジェクトを作成してワークフローを構築しましょう
                </p>
                <button
                  onClick={() => setShowProjectModal(true)}
                  className="bg-studio-500 text-white px-6 py-3 rounded-lg hover:bg-studio-600 transition-colors"
                >
                  新しいプロジェクトを作成
                </button>
              </div>
            </div>
          )}
          
          {/* 実行パネル */}
          {showExecutionPanel && (
            <ExecutionPanel 
              onClose={() => setShowExecutionPanel(false)}
            />
          )}
        </div>
      </div>

      {/* プロジェクト作成モーダル */}
      {showProjectModal && (
        <ProjectModal
          onClose={() => setShowProjectModal(false)}
          onSubmit={handleCreateProject}
        />
      )}

      {/* トースト通知 */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

export default App;